/**
 * 🎯 MÓDULO DE PROJETOS DE CONSULTORIA / CONSULTING PROJECTS MODULE
 * 
 * Sistema completo e personalizável de gestão de projetos de consultoria Lean,
 * com etapas configuráveis, roadmap interativo, kaizen weeks e auditorias.
 * 
 * Lean Consulting Default Stages:
 * 1. Estratégia - Workshop inicial + análise de mercado
 * 2. Gestão da Mudança - Apresentação do porquê Lean
 * 3. Tática - MFV + Kaizens potenciais + Roadmap
 * 4. Operacional - Kaizen weeks mensais
 * 5. Sustentação - Auditorias mensais de sustentação
 */

// ============================================================================
// TIPOS DE CONFIGURAÇÃO DE ETAPAS (Stage Config)
// ============================================================================

/**
 * Tipo de conteúdo que uma etapa pode ter
 */
export type StageContentType = 
  | 'file_upload'        // Upload de arquivos (apresentações, docs)
  | 'indicators'         // Indicadores-chave com metas
  | 'checklist'          // Checklist configurável
  | 'roadmap'            // Roadmap visual interativo
  | 'kaizen_week'        // Semana kaizen com cronograma
  | 'audit'              // Formulário de auditoria
  | 'notes'              // Notas / observações livres
  | 'team'               // Gestão de equipe
  | 'timeline'           // Linha do tempo / cronograma
  | 'action_plan';       // Plano de ação (5W2H)

/**
 * Status de uma etapa do projeto
 */
export type StageStatus = 
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'on_hold';

/**
 * Status de um projeto de consultoria
 */
export type ConsultingProjectStatus = 
  | 'draft'
  | 'active'
  | 'paused'
  | 'completed'
  | 'cancelled';

// ============================================================================
// CONFIGURAÇÃO DE ETAPAS PERSONALIZÁVEIS
// ============================================================================

/**
 * Definição de um campo de indicador
 */
export interface IndicatorDefinition {
  id: string;
  name: string;
  unit: string;              // Ex: "min", "%", "R$", "un/h"
  target?: number;
  baseline?: number;
  direction: 'up' | 'down';  // Se melhor é subir ou descer
  color?: string;
}

/**
 * Valor medido de um indicador
 */
export interface IndicatorValue {
  indicatorId: string;
  value: number;
  measuredAt: string;        // ISO date
  notes?: string;
}

/**
 * Item de checklist
 */
export interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
  completedAt?: string;
  completedBy?: string;
  dueDate?: string;
  responsible?: string;
  order: number;
}

/**
 * Arquivo anexado a uma etapa
 */
export interface StageFile {
  id: string;
  name: string;
  type: string;              // MIME type
  size: number;
  url?: string;              // URL do Supabase Storage ou base64
  uploadedAt: string;
  uploadedBy: string;
  category?: string;         // Ex: "apresentacao_conceito", "apresentacao_final", "mfv"
  description?: string;
}

/**
 * Configuração de conteúdo de uma etapa
 */
export interface StageContentConfig {
  type: StageContentType;
  label: string;             // Nome exibido ao usuário
  required: boolean;
  enabled: boolean;
  order: number;
  config?: Record<string, unknown>; // Configurações específicas do tipo
}

/**
 * Configuração de uma etapa do projeto (template)
 */
export interface ProjectStageConfig {
  id: string;
  name: string;
  description: string;
  icon: string;              // Nome do ícone lucide
  color: string;             // Cor tema da etapa
  order: number;
  contents: StageContentConfig[];
  isRepeatable: boolean;     // Se a etapa pode se repetir (ex: kaizen weeks)
  estimatedDuration?: string; // Ex: "1 semana", "1 mês"
}

/**
 * Template de projeto (coleção de etapas configuradas)
 */
export interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  stages: ProjectStageConfig[];
  createdBy: string;
  createdAt: string;
  isDefault: boolean;        // Template padrão do sistema
}

// ============================================================================
// DADOS DO PROJETO
// ============================================================================

/**
 * Instância de uma etapa no projeto (dados reais)
 */
export interface ProjectStageInstance {
  id: string;
  stageConfigId: string;
  projectId: string;
  name: string;              // Pode ser customizado ex: "Kaizen #3 - 5S Almoxarifado"
  status: StageStatus;
  startDate?: string;
  endDate?: string;
  completedAt?: string;
  
  // Dados da etapa
  files: StageFile[];
  indicators: IndicatorValue[];
  checklist: ChecklistItem[];
  notes: string;
  team: StageTeamMember[];
  actionItems: ActionPlanItem[];  // Plano de ação da etapa
  kaizenSchedules?: KaizenWeekSchedule[]; // Semanas Kaizen da etapa

  // Metadados
  order: number;
  parentStageId?: string;    // Para sub-etapas
  iteration?: number;        // Para etapas repetíveis (ex: kaizen #1, #2, #3)
  
  createdAt: string;
  updatedAt: string;
}

/**
 * Membro da equipe de uma etapa
 */
export interface StageTeamMember {
  id: string;
  name: string;
  role: string;              // Ex: "Líder Kaizen", "Champion", "Facilitador"
  email?: string;
  avatar?: string;
}

// ============================================================================
// PLANO DE AÇÃO (ACTION PLAN) - 5W2H
// ============================================================================

/**
 * Status de uma ação do plano de ação
 */
export type ActionPlanItemStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'late';

/**
 * Prioridade de uma ação
 */
export type ActionPlanItemPriority = 'low' | 'medium' | 'high' | 'critical';

/**
 * Item do plano de ação (5W2H)
 */
export interface ActionPlanItem {
  id: string;
  
  // 5W2H
  oQue: string;              // O quê? (What) - Descrição da ação
  porque: string;            // Por quê? (Why) - Justificativa
  onde: string;              // Onde? (Where) - Local/área
  quem: string;              // Quem? (Who) - Responsável
  quando: string;            // Quando? (When) - Prazo (ISO date)
  como: string;              // Como? (How) - Método/procedimento
  quanto: string;            // Quanto? (How much) - Custo/investimento
  
  // Controle
  status: ActionPlanItemStatus;
  priority: ActionPlanItemPriority;
  percentComplete: number;   // 0-100
  
  // Contexto
  category?: string;         // Categoria: qualidade, produtividade, segurança, etc.
  notes?: string;            // Observações extras
  
  // Metadados
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  
  order: number;
}

/**
 * Labels para prioridades
 */
export const ACTION_PRIORITY_LABELS: Record<ActionPlanItemPriority, { label: string; color: string }> = {
  low: { label: 'Baixa', color: '#64748B' },
  medium: { label: 'Média', color: '#F59E0B' },
  high: { label: 'Alta', color: '#F97316' },
  critical: { label: 'Crítica', color: '#EF4444' },
};

/**
 * Labels para status
 */
export const ACTION_STATUS_LABELS: Record<ActionPlanItemStatus, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pendente', color: '#64748B', bg: '#64748B15' },
  in_progress: { label: 'Em Andamento', color: '#3B82F6', bg: '#3B82F615' },
  completed: { label: 'Concluída', color: '#10B981', bg: '#10B98115' },
  cancelled: { label: 'Cancelada', color: '#94A3B8', bg: '#94A3B815' },
  late: { label: 'Atrasada', color: '#EF4444', bg: '#EF444415' },
};

// ============================================================================
// ROADMAP
// ============================================================================

/**
 * Item do roadmap (kaizen ou milestone planejado)
 */
export interface RoadmapItem {
  id: string;
  projectId: string;
  title: string;
  description: string;
  area: string;              // Ex: "Produção", "Logística", "Administrativo"
  type: 'kaizen' | 'milestone' | 'training' | 'audit' | 'review';
  status: 'planned' | 'in_progress' | 'completed' | 'postponed' | 'cancelled';
  
  // Datas
  plannedStart: string;
  plannedEnd: string;
  actualStart?: string;
  actualEnd?: string;
  
  // Relação com etapa
  stageInstanceId?: string;  // Link com a etapa real quando executado
  
  // Visual
  color?: string;
  icon?: string;
  lane?: number;             // Linha do roadmap (para swimlanes por área)
  
  // Dados
  team: string[];
  expectedGains?: string;
  linkedKaizenId?: string;   // Link com registro de kaizen no módulo de ganhos
  
  order: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// KAIZEN WEEK
// ============================================================================

/**
 * Cronograma da semana kaizen
 */
export interface KaizenWeekSchedule {
  id: string;
  stageInstanceId: string;
  
  // Dia da semana (seg a sex)
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';
  dayLabel?: string;         // Label customizado
  
  // Atividades do dia
  activities: KaizenDayActivity[];
}

/**
 * Atividade de um dia da semana kaizen
 */
export interface KaizenDayActivity {
  id: string;
  time: string;              // Ex: "08:00", "14:00"
  endTime?: string;
  title: string;
  description?: string;
  responsible?: string;
  location?: string;
  completed: boolean;
  type: 'training' | 'gemba' | 'workshop' | 'presentation' | 'implementation' | 'review' | 'other';
}

// ============================================================================
// AUDITORIA DE SUSTENTAÇÃO
// ============================================================================

/**
 * Critério de auditoria
 */
export interface AuditCriterion {
  id: string;
  name: string;
  description: string;
  order: number;
}

/**
 * Avaliação de um critério na auditoria
 */
export interface AuditScore {
  criterionId: string;
  score: number;             // 1 a 4
  notes?: string;
}

/**
 * Auditoria de sustentação de um kaizen
 */
export interface SustainabilityAudit {
  id: string;
  projectId: string;
  stageInstanceId?: string;  // Etapa do kaizen auditado
  roadmapItemId?: string;    // Item do roadmap relacionado
  linkedKaizenId?: string;   // Link com kaizen no módulo de ganhos
  
  // Identificação
  kaizenTitle: string;
  concept?: LeanConcept;     // Conceito Lean do kaizen auditado
  auditDate: string;
  auditor: string;
  area: string;
  
  // Avaliação - 4 critérios com notas de 1 a 4
  scores: AuditScore[];
  averageScore: number;
  
  // Ações corretivas
  correctiveActions?: string;
  nextAuditDate?: string;
  
  // Status
  status: 'pending' | 'completed' | 'action_required';
  
  createdAt: string;
  updatedAt: string;
}

/**
 * Tipo de conceito Lean aplicado em um kaizen
 */
export type LeanConcept =
  | 'trabalho_padronizado'
  | '5s'
  | 'kanban'
  | 'setup_rapido'
  | 'tpm'
  | 'jidoka'
  | 'fluxo_continuo'
  | 'poka_yoke'
  | 'heijunka'
  | 'gestao_visual'
  | 'value_stream'
  | 'custom';

/**
 * Labels dos conceitos Lean
 */
export const LEAN_CONCEPT_LABELS: Record<LeanConcept, string> = {
  trabalho_padronizado: 'Trabalho Padronizado',
  '5s': '5S',
  kanban: 'Kanban',
  setup_rapido: 'Setup Rápido (SMED)',
  tpm: 'TPM - Manutenção Produtiva Total',
  jidoka: 'Jidoka (Autonomação)',
  fluxo_continuo: 'Fluxo Contínuo',
  poka_yoke: 'Poka-Yoke',
  heijunka: 'Heijunka (Nivelamento)',
  gestao_visual: 'Gestão Visual',
  value_stream: 'Mapeamento de Fluxo de Valor',
  custom: 'Personalizado',
};

/**
 * Critérios de auditoria por conceito Lean.
 * Cada conceito possui 4 frases que descrevem níveis de 1 a 4.
 */
export interface ConceptAuditPhrase {
  score: number;          // 1 a 4
  description: string;    // Frase descritiva do nível
}

export interface ConceptAuditCriterion {
  id: string;
  name: string;
  phrases: ConceptAuditPhrase[];
  order: number;
}

/**
 * Critérios de auditoria específicos por conceito
 */
export const CONCEPT_AUDIT_CRITERIA: Record<LeanConcept, ConceptAuditCriterion[]> = {
  trabalho_padronizado: [
    {
      id: 'tp_padrao', name: 'Existência do Padrão', order: 1,
      phrases: [
        { score: 1, description: 'Não existe documento de trabalho padrão definido.' },
        { score: 2, description: 'Existe um padrão documentado, mas não está visível no posto de trabalho.' },
        { score: 3, description: 'Padrão documentado, visível e seguido pela maioria dos operadores.' },
        { score: 4, description: 'Padrão documentado, visível, seguido por todos e revisado periodicamente.' },
      ],
    },
    {
      id: 'tp_aderencia', name: 'Aderência ao Padrão', order: 2,
      phrases: [
        { score: 1, description: 'Os operadores não seguem o padrão estabelecido.' },
        { score: 2, description: 'Alguns operadores seguem parcialmente o padrão.' },
        { score: 3, description: 'A maioria segue o padrão com poucos desvios.' },
        { score: 4, description: 'Todos seguem o padrão consistentemente sem desvios.' },
      ],
    },
    {
      id: 'tp_resultados', name: 'Resultados e Indicadores', order: 3,
      phrases: [
        { score: 1, description: 'Indicadores não são monitorados ou pioraram significativamente.' },
        { score: 2, description: 'Indicadores monitorados, mas abaixo da meta do kaizen.' },
        { score: 3, description: 'Indicadores dentro da meta na maioria dos períodos.' },
        { score: 4, description: 'Indicadores consistentemente na meta ou acima, com melhoria contínua.' },
      ],
    },
    {
      id: 'tp_melhoria', name: 'Melhoria Contínua', order: 4,
      phrases: [
        { score: 1, description: 'Nenhuma sugestão de melhoria desde o kaizen.' },
        { score: 2, description: 'Sugestões esporádicas sem implementação.' },
        { score: 3, description: 'Equipe propõe melhorias e algumas foram implementadas.' },
        { score: 4, description: 'Ciclo PDCA ativo com melhorias regulares implementadas pela equipe.' },
      ],
    },
  ],
  '5s': [
    {
      id: '5s_seiri', name: 'Utilização (Seiri)', order: 1,
      phrases: [
        { score: 1, description: 'Muitos itens desnecessários espalhados na área.' },
        { score: 2, description: 'Alguns itens desnecessários ainda presentes.' },
        { score: 3, description: 'Área limpa com poucos itens sem identificação clara.' },
        { score: 4, description: 'Apenas itens necessários presentes, todos identificados e justificados.' },
      ],
    },
    {
      id: '5s_seiton', name: 'Organização (Seiton)', order: 2,
      phrases: [
        { score: 1, description: 'Materiais e ferramentas sem local definido.' },
        { score: 2, description: 'Locais definidos, mas nem todos seguem.' },
        { score: 3, description: 'Tudo com local definido e sinalizado, poucos desvios.' },
        { score: 4, description: 'Organização perfeita, gestão visual clara, qualquer desvio é imediatamente visível.' },
      ],
    },
    {
      id: '5s_seiso', name: 'Limpeza (Seiso)', order: 3,
      phrases: [
        { score: 1, description: 'Área visivelmente suja, sem rotina de limpeza.' },
        { score: 2, description: 'Limpeza ocasional, sem padrão definido.' },
        { score: 3, description: 'Rotina de limpeza definida e seguida na maioria das vezes.' },
        { score: 4, description: 'Área impecável, limpeza integrada à rotina, fontes de sujeira eliminadas.' },
      ],
    },
    {
      id: '5s_disciplina', name: 'Disciplina (Shitsuke)', order: 4,
      phrases: [
        { score: 1, description: 'Sem auditorias ou acompanhamento do 5S.' },
        { score: 2, description: 'Auditorias esporádicas sem ações de correção.' },
        { score: 3, description: 'Auditorias regulares com plano de ação para desvios.' },
        { score: 4, description: 'Cultura de 5S enraizada, equipe auto-gerenciada, melhoria contínua ativa.' },
      ],
    },
  ],
  kanban: [
    {
      id: 'kb_sinalizacao', name: 'Sinalização e Cartões', order: 1,
      phrases: [
        { score: 1, description: 'Sistema kanban não está funcionando, cartões ausentes.' },
        { score: 2, description: 'Cartões existem mas não são utilizados corretamente.' },
        { score: 3, description: 'Sistema kanban funcional com poucos desvios de uso.' },
        { score: 4, description: 'Sistema kanban perfeito, sem rupturas, fluxo puxado consistente.' },
      ],
    },
    {
      id: 'kb_niveis', name: 'Níveis de Estoque', order: 2,
      phrases: [
        { score: 1, description: 'Estoques descontrolados, sem respeito aos limites kanban.' },
        { score: 2, description: 'Respeito parcial aos limites, frequentes excessos ou faltas.' },
        { score: 3, description: 'Limites respeitados na maioria do tempo.' },
        { score: 4, description: 'Limites sempre respeitados, com revisão periódica dos níveis.' },
      ],
    },
    {
      id: 'kb_fluxo', name: 'Fluxo de Informação', order: 3,
      phrases: [
        { score: 1, description: 'Equipe não entende o funcionamento do kanban.' },
        { score: 2, description: 'Entendimento parcial, depende de poucos conhecedores.' },
        { score: 3, description: 'Maioria entende e opera o kanban corretamente.' },
        { score: 4, description: 'Todos entendem, operam e sugerem melhorias no sistema.' },
      ],
    },
    {
      id: 'kb_resultados', name: 'Resultados', order: 4,
      phrases: [
        { score: 1, description: 'Nível de serviço e giro de estoque pioraram.' },
        { score: 2, description: 'Resultados instáveis, ora melhores ora piores.' },
        { score: 3, description: 'Resultados mantidos próximos à meta.' },
        { score: 4, description: 'Resultados superando a meta com tendência de melhoria.' },
      ],
    },
  ],
  setup_rapido: [
    {
      id: 'sr_metodo', name: 'Método de Troca', order: 1,
      phrases: [
        { score: 1, description: 'Setup sem padrão, tempo voltou ao nível anterior ao kaizen.' },
        { score: 2, description: 'Padrão existe, mas tempo de setup aumentou parcialmente.' },
        { score: 3, description: 'Tempo de setup mantido próximo à meta do kaizen.' },
        { score: 4, description: 'Tempo de setup igual ou melhor que a meta, com novas melhorias.' },
      ],
    },
    {
      id: 'sr_separacao', name: 'Setup Interno/Externo', order: 2,
      phrases: [
        { score: 1, description: 'Não há separação entre setup interno e externo.' },
        { score: 2, description: 'Separação parcial, ainda há atividades externas feitas com máquina parada.' },
        { score: 3, description: 'Separação clara, maioria das atividades externas feitas antecipadamente.' },
        { score: 4, description: 'Separação completa, checklist seguido, preparação antecipada perfeita.' },
      ],
    },
    {
      id: 'sr_ferramental', name: 'Ferramental e Dispositivos', order: 3,
      phrases: [
        { score: 1, description: 'Ferramentas desorganizadas, dificuldade para encontrar.' },
        { score: 2, description: 'Ferramentas organizadas, mas nem sempre disponíveis.' },
        { score: 3, description: 'Kit de setup preparado e disponível para cada troca.' },
        { score: 4, description: 'Kit completo, dispositivos otimizados, troca sem busca de ferramentas.' },
      ],
    },
    {
      id: 'sr_treinamento', name: 'Treinamento da Equipe', order: 4,
      phrases: [
        { score: 1, description: 'Apenas 1-2 pessoas sabem fazer o setup.' },
        { score: 2, description: 'Equipe parcialmente treinada.' },
        { score: 3, description: 'Maioria treinada, matriz de habilidades atualizada.' },
        { score: 4, description: 'Toda equipe treinada, flexível, com capacitação cruzada.' },
      ],
    },
  ],
  tpm: [
    {
      id: 'tpm_autonoma', name: 'Manutenção Autônoma', order: 1,
      phrases: [
        { score: 1, description: 'Operadores não realizam manutenção autônoma.' },
        { score: 2, description: 'Checklist de manutenção existe mas não é seguido.' },
        { score: 3, description: 'Manutenção autônoma seguida, com registros de anomalias.' },
        { score: 4, description: 'Manutenção autônoma exemplar, operadores detectam anomalias antecipadamente.' },
      ],
    },
    {
      id: 'tpm_disponibilidade', name: 'Disponibilidade do Equipamento', order: 2,
      phrases: [
        { score: 1, description: 'Paradas não planejadas frequentes, OEE caiu.' },
        { score: 2, description: 'OEE instável, algumas paradas recorrentes.' },
        { score: 3, description: 'OEE mantido próximo à meta com poucas paradas.' },
        { score: 4, description: 'OEE na meta ou acima, zero paradas não planejadas.' },
      ],
    },
    {
      id: 'tpm_registro', name: 'Registro de Ocorrências', order: 3,
      phrases: [
        { score: 1, description: 'Não há registro de paradas e falhas.' },
        { score: 2, description: 'Registro parcial, sem análise de causa raiz.' },
        { score: 3, description: 'Registro completo com análise de causa para falhas críticas.' },
        { score: 4, description: 'Registro completo, análise de causa, ações preventivas ativas.' },
      ],
    },
    {
      id: 'tpm_visual', name: 'Gestão Visual do Equipamento', order: 4,
      phrases: [
        { score: 1, description: 'Nenhuma gestão visual no equipamento.' },
        { score: 2, description: 'Sinalização básica existe mas desatualizada.' },
        { score: 3, description: 'Gestão visual funcional com indicadores visíveis.' },
        { score: 4, description: 'Gestão visual completa, tendências visíveis, ações claras.' },
      ],
    },
  ],
  jidoka: [
    {
      id: 'jd_deteccao', name: 'Detecção de Anomalias', order: 1,
      phrases: [
        { score: 1, description: 'Defeitos não são detectados no processo, chegam ao cliente.' },
        { score: 2, description: 'Detecção parcial, alguns defeitos escapam.' },
        { score: 3, description: 'Sistema detecta a maioria das anomalias automaticamente.' },
        { score: 4, description: 'Detecção automática total, parada no ponto de causa.' },
      ],
    },
    {
      id: 'jd_resposta', name: 'Resposta a Problemas', order: 2,
      phrases: [
        { score: 1, description: 'Sem protocolo de resposta a problemas de qualidade.' },
        { score: 2, description: 'Protocolo existe, mas resposta é lenta.' },
        { score: 3, description: 'Resposta rápida com cadeia de ajuda definida.' },
        { score: 4, description: 'Cadeia de ajuda ativa, resposta imediata, resolução na causa raiz.' },
      ],
    },
    {
      id: 'jd_qualidade', name: 'Indicadores de Qualidade', order: 3,
      phrases: [
        { score: 1, description: 'Indicadores de qualidade pioraram desde o kaizen.' },
        { score: 2, description: 'Indicadores instáveis.' },
        { score: 3, description: 'Indicadores estáveis próximos à meta.' },
        { score: 4, description: 'Zero defeito ou tendência consistente de melhoria.' },
      ],
    },
    {
      id: 'jd_cultura', name: 'Cultura de Qualidade', order: 4,
      phrases: [
        { score: 1, description: 'Equipe não se preocupa com qualidade.' },
        { score: 2, description: 'Preocupação com qualidade apenas quando cobrada.' },
        { score: 3, description: 'Equipe busca qualidade proativamente na maioria das vezes.' },
        { score: 4, description: 'Cultura de qualidade na fonte, equipe para a linha quando necessário.' },
      ],
    },
  ],
  fluxo_continuo: [
    {
      id: 'fc_layout', name: 'Layout e Fluxo', order: 1,
      phrases: [
        { score: 1, description: 'Layout voltou à configuração anterior, fluxo interrompido.' },
        { score: 2, description: 'Layout mantido, mas com estoques intermediários crescendo.' },
        { score: 3, description: 'Fluxo contínuo mantido com poucos gargalos.' },
        { score: 4, description: 'Fluxo contínuo perfeito, peça-a-peça, sem estoques intermediários.' },
      ],
    },
    {
      id: 'fc_takt', name: 'Aderência ao Takt Time', order: 2,
      phrases: [
        { score: 1, description: 'Produção não segue o takt time.' },
        { score: 2, description: 'Takt time conhecido, mas não seguido consistentemente.' },
        { score: 3, description: 'Produção alinhada ao takt na maioria dos turnos.' },
        { score: 4, description: 'Produção perfeitamente alinhada ao takt com balanceamento ótimo.' },
      ],
    },
    {
      id: 'fc_wip', name: 'WIP (Estoque em Processo)', order: 3,
      phrases: [
        { score: 1, description: 'WIP cresceu significativamente.' },
        { score: 2, description: 'WIP acima do limite definido no kaizen.' },
        { score: 3, description: 'WIP controlado, dentro dos limites na maior parte do tempo.' },
        { score: 4, description: 'WIP mínimo, limites FIFO respeitados, fluxo visual claro.' },
      ],
    },
    {
      id: 'fc_operadores', name: 'Flexibilidade dos Operadores', order: 4,
      phrases: [
        { score: 1, description: 'Operadores fixos, sem multifuncionalidade.' },
        { score: 2, description: 'Alguma multifuncionalidade, mas limitada.' },
        { score: 3, description: 'Matriz de habilidades ativa, maioria multifuncional.' },
        { score: 4, description: 'Todos multifuncionais, rotação ativa, adaptação rápida à demanda.' },
      ],
    },
  ],
  poka_yoke: [
    {
      id: 'py_dispositivos', name: 'Dispositivos à Prova de Erro', order: 1,
      phrases: [
        { score: 1, description: 'Dispositivos poka-yoke não estão funcionando.' },
        { score: 2, description: 'Alguns dispositivos funcionam, outros foram desativados.' },
        { score: 3, description: 'Todos os dispositivos funcionais, manutenção em dia.' },
        { score: 4, description: 'Dispositivos funcionais, novos poka-yokes criados pela equipe.' },
      ],
    },
    {
      id: 'py_eficacia', name: 'Eficácia na Prevenção', order: 2,
      phrases: [
        { score: 1, description: 'Defeitos que o poka-yoke deveria prevenir continuam ocorrendo.' },
        { score: 2, description: 'Redução parcial dos defeitos.' },
        { score: 3, description: 'Defeitos-alvo eliminados na maioria dos casos.' },
        { score: 4, description: 'Zero defeitos do tipo prevenido, prevenção total.' },
      ],
    },
    {
      id: 'py_manutencao', name: 'Manutenção dos Dispositivos', order: 3,
      phrases: [
        { score: 1, description: 'Sem rotina de verificação dos dispositivos.' },
        { score: 2, description: 'Verificação ocasional, sem registro.' },
        { score: 3, description: 'Verificação periódica com registro de funcionamento.' },
        { score: 4, description: 'Verificação diária integrada ao checklist, calibração em dia.' },
      ],
    },
    {
      id: 'py_conhecimento', name: 'Conhecimento da Equipe', order: 4,
      phrases: [
        { score: 1, description: 'Equipe não sabe a função do poka-yoke.' },
        { score: 2, description: 'Conhecimento básico, sem entendimento do porquê.' },
        { score: 3, description: 'Equipe entende a função e reporta falhas.' },
        { score: 4, description: 'Equipe propõe novos poka-yokes e participa da melhoria.' },
      ],
    },
  ],
  heijunka: [
    {
      id: 'hj_nivelamento', name: 'Nivelamento da Produção', order: 1,
      phrases: [
        { score: 1, description: 'Produção em lotes grandes, sem nivelamento.' },
        { score: 2, description: 'Tentativa de nivelar, mas ainda com picos e vales.' },
        { score: 3, description: 'Produção nivelada na maioria dos períodos.' },
        { score: 4, description: 'Produção perfeitamente nivelada por volume e mix.' },
      ],
    },
    {
      id: 'hj_quadro', name: 'Quadro Heijunka', order: 2,
      phrases: [
        { score: 1, description: 'Quadro heijunka não existe ou abandonado.' },
        { score: 2, description: 'Quadro existe, mas não é atualizado diariamente.' },
        { score: 3, description: 'Quadro atualizado e seguido pela maioria.' },
        { score: 4, description: 'Quadro atualizado em tempo real, disparando produção puxada.' },
      ],
    },
    {
      id: 'hj_lotes', name: 'Tamanho dos Lotes', order: 3,
      phrases: [
        { score: 1, description: 'Lotes grandes, voltaram ao tamanho pré-kaizen.' },
        { score: 2, description: 'Lotes reduzidos, mas acima do ideal.' },
        { score: 3, description: 'Lotes no tamanho definido no kaizen.' },
        { score: 4, description: 'Lotes minimizados, próximo de peça-a-peça quando possível.' },
      ],
    },
    {
      id: 'hj_estabilidade', name: 'Estabilidade', order: 4,
      phrases: [
        { score: 1, description: 'Alto nível de horas extras e urgências.' },
        { score: 2, description: 'Horas extras reduzidas, mas ainda frequentes.' },
        { score: 3, description: 'Produção estável, poucas urgências.' },
        { score: 4, description: 'Produção previsível, sem urgências, entrega no prazo consistente.' },
      ],
    },
  ],
  gestao_visual: [
    {
      id: 'gv_quadros', name: 'Quadros e Indicadores', order: 1,
      phrases: [
        { score: 1, description: 'Quadros de gestão visual ausentes ou abandonados.' },
        { score: 2, description: 'Quadros existem, mas desatualizados.' },
        { score: 3, description: 'Quadros atualizados regularmente com indicadores-chave.' },
        { score: 4, description: 'Gestão visual completa, atualização diária, ações visíveis.' },
      ],
    },
    {
      id: 'gv_sinalizacao', name: 'Sinalização da Área', order: 2,
      phrases: [
        { score: 1, description: 'Sem sinalização de chão, áreas indefinidas.' },
        { score: 2, description: 'Sinalização parcial, algumas áreas demarcadas.' },
        { score: 3, description: 'Áreas demarcadas, fluxo sinalizado, locais de materiais definidos.' },
        { score: 4, description: 'Sinalização completa e padronizada, qualquer pessoa entende o fluxo.' },
      ],
    },
    {
      id: 'gv_anomalias', name: 'Visibilidade de Anomalias', order: 3,
      phrases: [
        { score: 1, description: 'Problemas não são visíveis, só descobertos quando críticos.' },
        { score: 2, description: 'Alguns problemas visíveis, mas sem sistema estruturado.' },
        { score: 3, description: 'Andons ou sistema visual para detectar anomalias funcionando.' },
        { score: 4, description: 'Qualquer anomalia é imediatamente visível e tratada.' },
      ],
    },
    {
      id: 'gv_rotina', name: 'Rotina de Gestão', order: 4,
      phrases: [
        { score: 1, description: 'Sem reuniões de gestão com base nos quadros visuais.' },
        { score: 2, description: 'Reuniões esporádicas sem rotina definida.' },
        { score: 3, description: 'Reuniões diárias com base nos quadros de gestão visual.' },
        { score: 4, description: 'Reuniões por camadas (operacional, tática, estratégica) integradas.' },
      ],
    },
  ],
  value_stream: [
    {
      id: 'vs_mapa', name: 'Mapa do Fluxo de Valor', order: 1,
      phrases: [
        { score: 1, description: 'MFV desatualizado ou inexistente.' },
        { score: 2, description: 'MFV existe, mas não reflete a situação atual.' },
        { score: 3, description: 'MFV atualizado, ações do plano em andamento.' },
        { score: 4, description: 'MFV revisado periodicamente, plano de ação atualizado com responsáveis.' },
      ],
    },
    {
      id: 'vs_lead_time', name: 'Lead Time', order: 2,
      phrases: [
        { score: 1, description: 'Lead time voltou ao nível anterior ao mapeamento.' },
        { score: 2, description: 'Lead time parcialmente reduzido, mas acima da meta.' },
        { score: 3, description: 'Lead time na meta do estado futuro.' },
        { score: 4, description: 'Lead time abaixo da meta, melhorias adicionais implementadas.' },
      ],
    },
    {
      id: 'vs_desperdicios', name: 'Eliminação de Desperdícios', order: 3,
      phrases: [
        { score: 1, description: 'Desperdícios identificados não foram eliminados.' },
        { score: 2, description: 'Alguns desperdícios eliminados, outros retornaram.' },
        { score: 3, description: 'Maioria dos desperdícios eliminados e sustentados.' },
        { score: 4, description: 'Desperdícios eliminados, equipe identifica novos proativamente.' },
      ],
    },
    {
      id: 'vs_engajamento', name: 'Engajamento Interfuncional', order: 4,
      phrases: [
        { score: 1, description: 'Áreas trabalham isoladas, sem visão de fluxo.' },
        { score: 2, description: 'Alguma colaboração entre áreas do fluxo.' },
        { score: 3, description: 'Áreas colaboram com foco no fluxo de valor.' },
        { score: 4, description: 'Gestão por fluxo de valor, KPIs integrados, melhoria cross-funcional.' },
      ],
    },
  ],
  custom: [
    {
      id: 'custom_padrao', name: 'Padrão Seguido', order: 1,
      phrases: [
        { score: 1, description: 'Os padrões definidos durante o kaizen não estão sendo seguidos.' },
        { score: 2, description: 'Aderência parcial aos padrões definidos.' },
        { score: 3, description: 'Padrões seguidos na maioria das situações.' },
        { score: 4, description: 'Padrões seguidos consistentemente com revisões periódicas.' },
      ],
    },
    {
      id: 'custom_resultados', name: 'Resultados Mantidos', order: 2,
      phrases: [
        { score: 1, description: 'Os indicadores-chave pioraram desde o kaizen.' },
        { score: 2, description: 'Indicadores instáveis, oscilando em torno da meta.' },
        { score: 3, description: 'Indicadores dentro da meta na maioria dos períodos.' },
        { score: 4, description: 'Indicadores superando a meta com tendência de melhoria contínua.' },
      ],
    },
    {
      id: 'custom_visual', name: 'Gestão Visual', order: 3,
      phrases: [
        { score: 1, description: 'Quadros de gestão visual ausentes ou abandonados.' },
        { score: 2, description: 'Gestão visual básica, parcialmente atualizada.' },
        { score: 3, description: 'Gestão visual funcional e atualizada.' },
        { score: 4, description: 'Gestão visual exemplar, integrada à rotina de gestão.' },
      ],
    },
    {
      id: 'custom_engajamento', name: 'Engajamento da Equipe', order: 4,
      phrases: [
        { score: 1, description: 'Equipe não demonstra engajamento com as melhorias.' },
        { score: 2, description: 'Engajamento quando cobrada pela liderança.' },
        { score: 3, description: 'Equipe engajada, propõe melhorias esporadicamente.' },
        { score: 4, description: 'Equipe autônoma, propõe e implementa melhorias continuamente.' },
      ],
    },
  ],
};

/**
 * Configuração padrão de critérios de auditoria (genérico)
 */
export const DEFAULT_AUDIT_CRITERIA: AuditCriterion[] = [
  {
    id: 'standard',
    name: 'Padrão Seguido',
    description: 'Os padrões definidos durante o kaizen estão sendo seguidos pela equipe?',
    order: 1,
  },
  {
    id: 'results',
    name: 'Resultados Mantidos',
    description: 'Os indicadores-chave estão dentro da meta estabelecida no kaizen?',
    order: 2,
  },
  {
    id: 'visual',
    name: 'Gestão Visual',
    description: 'Os quadros de gestão visual, 5S e sinalizações estão atualizados?',
    order: 3,
  },
  {
    id: 'engagement',
    name: 'Engajamento da Equipe',
    description: 'A equipe demonstra engajamento e propriedade sobre as melhorias?',
    order: 4,
  },
];

// ============================================================================
// PROJETO DE CONSULTORIA (entidade principal)
// ============================================================================

/**
 * Projeto de Consultoria Lean
 */
// ============================================================================
// LINKED ITEMS — Cross-module project association
// ============================================================================

export type LinkedItemType = 'map' | 'action_plan' | 'blueprint' | 'workflow';

/** Modules that can be enabled/disabled per project */
export type ProjectModule = 'map' | 'action_plan' | 'stages' | 'roadmap' | 'audits' | 'indicators';

/** Reference linking a ConsultingProject to an object in another module */
export interface LinkedProjectItem {
  id: string;
  type: LinkedItemType;
  referenceId: string;    // ID of the linked object (e.g. VSM project id)
  referenceName?: string; // Display name cached at link time
  addedAt: string;
}

// ============================================================================
// MAIN PROJECT INTERFACE
// ============================================================================

export interface ConsultingProject {
  id: string;
  organizationId: string;

  // Tipo do projeto
  projectType: 'quick' | 'full_consulting';  // 'quick' = Pasta VSM+5W2H; 'full_consulting' = Jornada Lean completa

  // Módulos habilitados (selecionados na criação)
  enabledModules?: ProjectModule[];  // undefined = all modules for backwards compat
  
  // Identificação
  name: string;
  client: string;
  description: string;
  sector?: string;           // Setor / área do cliente
  
  // Template & configuração
  templateId: string;
  stageConfigs: ProjectStageConfig[];  // Cópia local das configs (personalizável)
  auditCriteria: AuditCriterion[];     // Critérios de auditoria personalizados
  
  // Etapas (instâncias reais)
  stages: ProjectStageInstance[];
  
  // Roadmap
  roadmapItems: RoadmapItem[];
  
  // Auditorias
  audits: SustainabilityAudit[];
  
  // Plano de ação do projeto (nível geral)
  actionItems: ActionPlanItem[];
  
  // Equipe do projeto
  projectTeam: StageTeamMember[];
  projectLeader?: string;
  
  // Status e datas
  status: ConsultingProjectStatus;
  startDate?: string;
  endDate?: string;
  deadline?: string;     // Target completion date (for ATRASADO indicator)

  // Metadados
  createdAt: string;
  updatedAt: string;
  createdBy: string;

  // Link com outros módulos
  linkedProjectId?: string;  // Link com projeto do VSM (legacy - single link)
  linkedItems?: LinkedProjectItem[]; // Links multi-module (future expansion)
  linkedGainsIds?: string[]; // Links com kaizens do módulo de ganhos
}

// ============================================================================
// TEMPLATE PADRÃO LEAN CONSULTING
// ============================================================================

/**
 * Template padrão para projetos de consultoria Lean
 */
export const DEFAULT_LEAN_TEMPLATE: ProjectTemplate = {
  id: 'lean-consulting-default',
  name: 'Consultoria Lean',
  description: 'Projeto completo de consultoria Lean com 5 etapas: Estratégia, Gestão da Mudança, Tática, Operacional e Sustentação.',
  isDefault: true,
  createdBy: 'system',
  createdAt: new Date().toISOString(),
  stages: [
    {
      id: 'strategy',
      name: 'Estratégia',
      description: 'Workshop inicial Lean com análise de mercado (concorrentes, clientes e fornecedores) para sensibilizar a liderança.',
      icon: 'Target',
      color: '#3B82F6',      // blue-500
      order: 1,
      isRepeatable: false,
      estimatedDuration: '1-2 semanas',
      contents: [
        { type: 'team', label: 'Equipe do Workshop', required: false, enabled: true, order: 1 },
        { type: 'timeline', label: 'Cronograma', required: false, enabled: true, order: 2 },
        { type: 'indicators', label: 'Indicadores-Chave', required: false, enabled: true, order: 3 },
        { type: 'file_upload', label: 'Apresentação do Workshop', required: true, enabled: true, order: 4 },
        { type: 'notes', label: 'Observações', required: false, enabled: true, order: 5 },
      ],
    },
    {
      id: 'change-management',
      name: 'Gestão da Mudança',
      description: 'Comunicação para toda a empresa sobre por que precisamos do Lean e como será o projeto.',
      icon: 'Users',
      color: '#8B5CF6',      // violet-500
      order: 2,
      isRepeatable: false,
      estimatedDuration: '1 semana',
      contents: [
        { type: 'team', label: 'Equipe de Comunicação', required: false, enabled: true, order: 1 },
        { type: 'file_upload', label: 'Apresentação', required: true, enabled: true, order: 2 },
        { type: 'checklist', label: 'Checklist de Comunicação', required: false, enabled: true, order: 3 },
        { type: 'notes', label: 'Observações', required: false, enabled: true, order: 4 },
      ],
    },
    {
      id: 'tactical',
      name: 'Tática',
      description: 'Semana de MFV (Mapeamento de Fluxo de Valor), identificação de kaizens potenciais e definição do roadmap.',
      icon: 'Map',
      color: '#F59E0B',      // amber-500
      order: 3,
      isRepeatable: false,
      estimatedDuration: '1-2 semanas',
      contents: [
        { type: 'team', label: 'Equipe da Semana MFV', required: false, enabled: true, order: 1 },
        { type: 'file_upload', label: 'Apresentação de Conceito', required: true, enabled: true, order: 2 },
        { type: 'file_upload', label: 'Apresentação MFV', required: true, enabled: true, order: 3 },
        { type: 'checklist', label: 'Checklist Pré-Kaizen', required: true, enabled: true, order: 4 },
        { type: 'roadmap', label: 'Roadmap do Projeto', required: true, enabled: true, order: 5 },
        { type: 'notes', label: 'Observações', required: false, enabled: true, order: 6 },
      ],
    },
    {
      id: 'operational',
      name: 'Operacional',
      description: 'Execução mensal de kaizens de 1 semana conforme roadmap. Cada kaizen inclui checklist, cronograma e resultados.',
      icon: 'Wrench',
      color: '#10B981',      // emerald-500
      order: 4,
      isRepeatable: true,    // Múltiplas semanas kaizen
      estimatedDuration: '1 semana por kaizen',
      contents: [
        { type: 'team', label: 'Equipe do Kaizen', required: true, enabled: true, order: 1 },
        { type: 'checklist', label: 'Checklist Pré-Kaizen', required: true, enabled: true, order: 2 },
        { type: 'kaizen_week', label: 'Cronograma da Semana', required: true, enabled: true, order: 3 },
        { type: 'file_upload', label: 'Apresentação de Conceito', required: true, enabled: true, order: 4 },
        { type: 'indicators', label: 'Indicadores (Antes/Depois)', required: true, enabled: true, order: 5 },
        { type: 'file_upload', label: 'Apresentação Final (Resultados)', required: true, enabled: true, order: 6 },
        { type: 'action_plan', label: 'Plano de Ação', required: false, enabled: true, order: 7 },
        { type: 'notes', label: 'Lições Aprendidas', required: false, enabled: true, order: 8 },
      ],
    },
    {
      id: 'sustainment',
      name: 'Sustentação',
      description: 'Auditorias mensais para verificar a sustentação dos kaizens realizados. Formulário com 4 critérios, notas de 1 a 4.',
      icon: 'ShieldCheck',
      color: '#EF4444',      // red-500
      order: 5,
      isRepeatable: true,    // Auditorias recorrentes
      estimatedDuration: 'Mensal',
      contents: [
        { type: 'audit', label: 'Auditoria de Sustentação', required: true, enabled: true, order: 1 },
        { type: 'indicators', label: 'Indicadores Monitorados', required: false, enabled: true, order: 2 },
        { type: 'action_plan', label: 'Plano de Ação Corretivo', required: false, enabled: true, order: 3 },
        { type: 'notes', label: 'Observações', required: false, enabled: true, order: 4 },
      ],
    },
  ],
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Calcula o progresso geral do projeto (0-100%)
 */
export function calculateProjectProgress(project: ConsultingProject): number {
  if (project.stages.length === 0) return 0;
  const completed = project.stages.filter(s => s.status === 'completed').length;
  return Math.round((completed / project.stages.length) * 100);
}

/**
 * Calcula a média das auditorias para um kaizen
 */
export function calculateAuditAverage(audit: SustainabilityAudit): number {
  if (audit.scores.length === 0) return 0;
  const total = audit.scores.reduce((sum, s) => sum + s.score, 0);
  return Number((total / audit.scores.length).toFixed(1));
}

/**
 * Retorna a cor de status com base na nota da auditoria
 */
export function getAuditColor(score: number): string {
  if (score >= 4) return '#10B981';    // green (nota 4)
  if (score >= 3) return '#3B82F6';    // blue (nota 3-4)
  if (score >= 2) return '#F59E0B';    // yellow (nota 2-3)
  return '#EF4444';                     // red (nota 1-2)
}

/**
 * Gera um ID único
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}
